<template>
  <div class="component">
    <router-view />

    <Stack
      class="stk-p-6"
      gap="4"
    >
      <Level id="entity-header">
        <LevelLeft>
          <Title size="7">
            People
          </Title>
        </LevelLeft>

        <LevelRight>
          <Buttons>
            <Button
              variant="primary"
              icon-after="fas fa-plus"
              :disabled="isLoading"
              size="3_5"
              @click="showAddModal = true"
            >
              <span>Add a Person</span>
            </Button>
          </Buttons>
        </LevelRight>
      </Level>

      <div>
        <Buttons
          class="filters"
          gap="6"
        >
          <form
            class="search-form stk-resize-3_5"
            @submit.prevent="handleSearch"
          >
            <input
              v-model="preferencesStore.peopleFilters.search"
              type="text"
              class="stk-input search-input"
              placeholder="Search by name or email"
              :disabled="isLoading"
            >
            <Button
              variant="primary"
              :disabled="isLoading"
            >
              Search
            </Button>
            <Button
              type="button"
              :disabled="isLoading"
              @click="handleClear"
            >
              Clear
            </Button>
          </form>

          <div class="options">
            <label class="option">
              <input
                v-model="preferencesStore.peopleFilters.type"
                type="radio"
                :disabled="isLoading"
                value=""
              >
              All
            </label>
            <label class="option">
              <input
                v-model="preferencesStore.peopleFilters.type"
                type="radio"
                :disabled="isLoading"
                value="Prospect"
              >
              <Badge
                resizable
                status="info"
              >Prospect</Badge>
            </label>
            <label class="option">
              <input
                v-model="preferencesStore.peopleFilters.type"
                type="radio"
                :disabled="isLoading"
                value="Client"
              >
              <Badge
                resizable
                status="success"
              >Client</Badge>
            </label>
          </div>

          <div
            v-if="advisorStore.featureFlags.includes('FAFN Direct Indexing')"
            class="options"
          >
            <label class="option">
              <input
                v-model="preferencesStore.peopleFilters.hasVadis"
                type="checkbox"
                :disabled="isLoading"
                :value="true"
              >
              Has VADIS
            </label>
          </div>

          <div
            v-if="advisorStore.featureFlags.includes('Teams')"
            class="options"
          >
            <label class="option">
              <input
                v-model="preferencesStore.peopleFilters.hideSharedWithMe"
                type="checkbox"
                :disabled="isLoading"
                :value="true"
              >
              Hide shared with me
            </label>
          </div>

          <Button
            icon-before="fas fa-redo-alt"
            size="3_5"
            spacing="tight"
            :disabled="isLoading"
            @click="handleReset"
          >
            <span>Reset Filters</span>
          </Button>
        </Buttons>
      </div>
    </Stack>

    <div class="people-table">
      <PeopleTable
        :has-fetched="advisorStore.people.hasFetched"
        :loading="isLoading"
        :people="advisorStore.people"
        :columns="columns"
        :sort="advisorStore.people.sort"
        :dir="advisorStore.people.dir"
        @onPrevious="handlePrevious"
        @onNext="handleNext"
        @onPageClick="handlePageClick"
        @onReset="handleReset"
        @onColumnSort="handleColumnSort"
      />
    </div>

    <StakeModal v-model="showAddModal">
      <NewPersonModal
        @onClose="showAddModal = false"
        @onAdd="handleReset"
      />
    </StakeModal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import NewPersonModal from '@frontend/ui/features/people/NewPersonModal.vue';
import PeopleTable from '@frontend/ui/features/people/PeopleTable.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { usePreferencesStore } from '@stores/preferencesStore';

import {
  Badge,
  Button,
  Buttons,
  Icon,
  Level,
  LevelLeft,
  LevelRight,
  Stack,
  StakeModal,
  StakeTooltip,
  Title,
} from '@stakeui';

const advisorStore = useAdvisorStore();
const preferencesStore = usePreferencesStore();
const showAddModal = ref(false);

const isLoading = computed(() => {
  return advisorStore.people.isFetching || !advisorStore.people.hasFetched;
});

const performSearch = () => {
  advisorStore.fetchPeople(preferencesStore.peopleFilters);
};

onMounted(() => {
  if (advisorStore.people.hasFetched) {
    return;
  }

  performSearch();
});

const handlePrevious = () => {
  if (!advisorStore.people.hasPrevious) {
    return;
  }

  preferencesStore.peopleFilters.page = advisorStore.people.page - 1;
  performSearch();
};

const handleNext = () => {
  if (!advisorStore.people.hasNext) {
    return;
  }

  preferencesStore.peopleFilters.page = advisorStore.people.page + 1;
  performSearch();
};

const handlePageClick = page => {
  preferencesStore.peopleFilters.page = page;
  performSearch();
};

const handleReset = () => {
  preferencesStore.resetPeopleFilters();
  performSearch();
};

const handleColumnSort = (sortable, columnId, defaultDir) => {
  if (!sortable) {
    return;
  }
  if (isLoading.value) {
    return;
  }

  let newDir = defaultDir;

  // We're clicking on the same column,
  // so we're just flipping the direction
  if (advisorStore.people.sort == columnId) {
    newDir = advisorStore.people.dir === 'asc' ? 'desc' : 'asc';
  }

  preferencesStore.peopleFilters.sort = columnId;
  preferencesStore.peopleFilters.dir = newDir;
  performSearch();
};

const handleClear = () => {
  preferencesStore.peopleFilters.search = '';
  performSearch();
};

const handleSearch = () => {
  if (!preferencesStore.peopleFilters.search) {
    return;
  }

  // Reset to the first page
  preferencesStore.peopleFilters.page = 1;
  performSearch();
};

watch(
    [
      () => preferencesStore.peopleFilters.type,
      () => preferencesStore.peopleFilters.hideSharedWithMe,
      () => preferencesStore.peopleFilters.hasVadis,
    ],
    () => {
      preferencesStore.peopleFilters.page = 1;
      performSearch();
    },
);

const columns = computed(() => {
  const cols = [
    { id: 'name', label: 'Name', sortable: true, defaultDir: 'asc' },
    { id: 'entity_type', label: 'Type', sortable: true, defaultDir: 'asc' },
    { id: 'owner', label: 'Owner' },
    { id: 'integrations', label: 'Linked To' },
  ];
  if (advisorStore.featureFlags.includes('FAFN Direct Indexing')) {
    cols.push({ id: 'vadis_status', label: 'VADIS Status', sortable: true, defaultDir: 'desc' });
  }
  cols.push(
    { id: 'created_at', label: 'Created', sortable: true, defaultDir: 'desc' },
    { id: 'updated_at', label: 'Updated', sortable: true, defaultDir: 'desc' },
  );
  return cols;
});
</script>

<style scoped>
.component {
  background-color: var(--stk-scheme-main);
}

.filters {
  row-gap: 0.25em;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 0.75em;
}

.search-input {
  min-width: 16em;
}

.options,
.option {
  display: flex;
  align-items: center;
}

.options {
  gap: 0.5em;
}

.option {
  cursor: pointer;
  gap: 0.25em;
  font-size: 0.875em;
}
</style>
